import { useCallback, useContext } from 'react'
import api from '../../../api'
import { Campaign, JourneyStepType, Template } from '../../../types'
import { EntityIdPicker } from '../../../ui/form/EntityIdPicker'
import { ActionStepIcon } from '../../../ui/icons'
import { CampaignForm } from '../../campaign/form/CampaignForm'
import { useResolver } from '../../../hooks'
import { useTranslation } from 'react-i18next'
import { ChannelIcon } from '../../campaign/ui/ChannelTag'
import Preview from '../../../ui/Preview'
import { SingleSelect } from '../../../ui/form/SingleSelect'
import { Heading, LinkButton } from '../../../ui'
import { TemplateContextProvider } from '../../campaign/template/TemplateContextProvider'
import { TemplateContext } from '../../../contexts'

interface ActionConfig {
    campaign_id: number
}

interface JourneyEditorTemplatePreviewProps {
    template: Template
    projectId: number
    size?: 'small' | 'large'
}

const ActionTemplatePreview = ({ template: { id, type, data, locale }, projectId, size = 'small' }: JourneyEditorTemplatePreviewProps) => {
    const [prerender] = useResolver(useCallback(async () => await api.templates.prerender(projectId, id), [id]))
    return (
        <Preview template={{ type, data: prerender?.data ?? data, locale }} size={size} />
    )
}

const JourneyEditTemplatePreview = ({ campaign }: { campaign: Campaign }) => {
    const { t } = useTranslation()
    const { variants, locales, currentLocale, currentTemplate, setTemplate, setLocale } = useContext(TemplateContext)
    return <>
        <Heading
            title={t('preview')}
            size="h4"
            actions={
                <>
                    {variants.length > 1 && <SingleSelect
                        options={variants}
                        size="small"
                        value={currentTemplate}
                        onChange={(variant) => setTemplate(variant)}
                    />}
                    <SingleSelect
                        options={locales}
                        size="small"
                        value={currentLocale}
                        onChange={(locale) => setLocale(locale)}
                    />
                    <LinkButton
                        to={`/projects/${campaign.project_id}/campaigns/${campaign.id}`}
                        size="small"
                        target="_blank"
                    >
                        {t('edit_campaign')}
                    </LinkButton>
                </>
            }
        />
        {currentTemplate && <ActionTemplatePreview
            template={currentTemplate}
            projectId={campaign.project_id}
            size="large"
        />}
    </>
}

export const actionStep: JourneyStepType<ActionConfig> = {
    name: 'send',
    icon: <ActionStepIcon />,
    category: 'action',
    description: 'send_desc',
    Describe({
        project: { id: projectId },
        value: {
            campaign_id,
        },
    }) {
        const { t } = useTranslation()
        const [campaign] = useResolver(useCallback(async () => {
            if (campaign_id) {
                return await api.campaigns.get(projectId, campaign_id)
            }
            return null
        }, [projectId, campaign_id]))
        const template = campaign?.templates?.[0]

        return (
            <>
                <div className="journey-step-body-name">
                    <div className="journey-step-action-type">
                        {campaign && <ChannelIcon channel={campaign.channel} />}
                    </div>
                    {campaign?.name ?? <>&#8211;</>}
                </div>
                <div className="journey-step-action-preview">
                    { campaign && template
                        ? <ActionTemplatePreview template={template} projectId={projectId} />
                        : (
                            <div className="journey-step-action-preview-placeholder">{t('journey_campaign_create_preview')}</div>
                        )}
                </div>
            </>
        )
    },
    newData: async () => ({
        campaign_id: 0,
    }),
    Edit({
        project: { id: projectId },
        onChange,
        value,
        disabled,
    }) {
        const [campaign] = useResolver(useCallback(async () => {
            if (value) {
                return await api.campaigns.get(projectId, value.campaign_id)
            }
            return null
        }, [projectId, value]))

        const { t } = useTranslation()
        return (
            <>
                <EntityIdPicker
                    label={t('campaign')}
                    subtitle={t('send_campaign_desc')}
                    get={useCallback(async id => await api.campaigns.get(projectId, id), [projectId])}
                    search={useCallback(async q => await api.campaigns.search(projectId, {
                        q,
                        limit: 50,
                        filter: { type: ['trigger'], all_folders: true },
                    }), [projectId])}
                    value={value.campaign_id}
                    onChange={campaign_id => onChange({ ...value, campaign_id })}
                    required
                    createModalSize="large"
                    renderCreateForm={onCreated => (
                        <CampaignForm
                            type="trigger"
                            onSave={onCreated}
                        />
                    )}
                    disabled={disabled}
                />

                {campaign && <TemplateContextProvider campaign={campaign} setCampaign={() => {}}>
                    <JourneyEditTemplatePreview campaign={campaign} />
                </TemplateContextProvider>}
            </>
        )
    },
    hasDataKey: true,
}
